import type { SalesNarrativeContent } from "@expert/contentful";

import type { Narrative, NarrativeBeat } from "./types";

import { narrativeFromContent } from "./narrativeFromContent";

/**
 * The approved VHDP (Verizon Home Device Protect) copy, as it stands in Contentful `3002` — used by
 * Storybook and the sales harness.
 *
 * Kept as CONTENT rather than as a hand-written `Narrative`, and run through the same
 * `narrativeFromContent` the app uses, so the fixture cannot drift from the production shape. A
 * hand-written narrative previously diverged on both axes that matter: invented beat ids
 * (`offer-1` vs the real `3002.offer-1`) and `generated` segments, where everything from the CMS is
 * `verbatim` and therefore not skippable.
 */
export const vhdpNarrativeContent: SalesNarrativeContent = {
    productSku: "3002",
    transition: {
        key: "3002.transition",
        text: "I'd like to talk to you about a separate coverage from your mobile device insurance called Verizon Home Device Protect.",
    },
    guide: {
        key: "3002.guide",
        text: "I'll send a link to your phone so we can look at this together — you'll see everything on your screen as I walk you through the coverage.",
    },
    offerBenefits: [
        {
            key: "3002.offer-1",
            text: "It covers all of your eligible products in your home for one monthly price.",
        },
        {
            key: "3002.offer-2",
            text: "Products you currently own are covered, and anything you buy later is included automatically — no receipts or registration needed.",
        },
        {
            key: "3002.offer-3",
            text: "There's a $99 service fee plus tax per approved claim, and we'll repair, replace, or reimburse you.",
        },
    ],
    close: { key: "3002.close", text: "It's $25 plus tax, per month." },
    enroll: { key: "3002.enroll", text: "May I start your enrollment?" },
};

/**
 * A deliberately DIFFERENT second narrative for the dev-tools Event Emitter, so a replacement is
 * observable rather than a no-op swap. Beat ids come from content keys, so this changes all three
 * things at once: surviving ids (`transition`/`guide`/`close`/`enroll`/`offer-1`) carry their
 * covered/skipped status, `offer-2` is DROPPED, and `offer-4` is NEW and starts pending.
 */
export const vhdpVariantNarrativeContent: SalesNarrativeContent = {
    ...vhdpNarrativeContent,
    transition: {
        key: "3002.transition",
        text: "[VARIANT] Before we wrap up, there's a separate home coverage I'd like to walk you through.",
    },
    offerBenefits: [
        {
            key: "3002.offer-1",
            text: "[VARIANT] One monthly price covers every eligible product in your home.",
        },
        {
            key: "3002.offer-3",
            text: "[VARIANT] A $99 service fee plus tax applies per approved claim.",
        },
        {
            key: "3002.offer-4",
            text: "[VARIANT] This beat is new in the replacement, so it starts pending.",
        },
    ],
};

/** The same content the app would render, assembled the same way. */
export const vhdpNarrative: Narrative = narrativeFromContent(vhdpNarrativeContent);

/** The variant, assembled the same way. */
export const vhdpVariantNarrative: Narrative = narrativeFromContent(vhdpVariantNarrativeContent);

/**
 * Beats with MIXED segments, for the beat-card stories only — deliberately not derived from the
 * content above, which is all `verbatim` because everything in the CMS is approved copy.
 *
 * Still worth having: a producer-delivered narrative wraps verbatim copy in its own connective prose,
 * so the card has to render woven, generated-only and verbatim-only beats. Synthetic on purpose, and
 * kept separate so nobody mistakes it for the production shape.
 */
export const mixedSegmentBeats: NarrativeBeat[] = [
    {
        id: "mixed.woven",
        index: 1,
        of: 3,
        segments: [
            { kind: "generated", text: "Since you mentioned the smart TV," },
            { kind: "verbatim", text: "it covers all of your eligible products in your home for one monthly price." },
        ],
    },
    {
        id: "mixed.generated-only",
        index: 2,
        of: 3,
        segments: [{ kind: "generated", text: "Worth mentioning while we have the account open." }],
    },
    {
        id: "mixed.verbatim-only",
        index: 3,
        of: 3,
        segments: [{ kind: "verbatim", text: "It's $25 plus tax, per month." }],
    },
];

/** Beat ids in render order. Derived, so stories can reference beats without hardcoding an id. */
export const vhdpBeatIds: string[] = vhdpNarrative.steps.flatMap((step) => step.beats.map((beat) => beat.id));
